// File: utils/useConsentChannels.js
import { useEffect, useState } from 'react';
import { fetchWrapper } from '../../../helpers';
import { CONSENT_CHANNELS } from './consentChannels';

/**
 * Channels for the consent nodes, loaded from /channel/list instead of the hard-coded
 * list — a customer's enabled channels are account-specific and change over time, so a
 * baked-in list eventually offers channels they do not have (or hides ones they do).
 *
 * Cached at module scope: the properties panel remounts every time a node is selected,
 * and the list does not change within a session.
 */
let cachedChannels = null;
let inFlight = null;

const normalize = (res) => {
  const raw = Array.isArray(res?.data) ? res.data : res?.data?.channels || [];
  return raw
    .map((c) => ({ id: String(c?.id ?? ''), label: c?.name || c?.label || '' }))
    .filter((c) => c.id && c.label);
};

export const loadConsentChannels = async () => {
  if (cachedChannels) return cachedChannels;
  if (!inFlight) {
    inFlight = fetchWrapper
      .get(`${process.env.REACT_APP_API_URL}/channel/list`)
      .then((res) => {
        const list = normalize(res);
        // Keep the static list as the fallback rather than rendering an empty dropdown.
        cachedChannels = list.length ? list : CONSENT_CHANNELS.map(({ id, label }) => ({ id, label }));
        return cachedChannels;
      })
      .catch((err) => {
        console.error('Failed to load channels for consent node:', err);
        return CONSENT_CHANNELS.map(({ id, label }) => ({ id, label }));
      })
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
};

export default function useConsentChannels() {
  const [channels, setChannels] = useState(
    () => cachedChannels || CONSENT_CHANNELS.map(({ id, label }) => ({ id, label }))
  );
  const [loading, setLoading] = useState(!cachedChannels);

  useEffect(() => {
    let active = true;
    if (cachedChannels) {
      setChannels(cachedChannels);
      setLoading(false);
      return undefined;
    }
    loadConsentChannels().then((list) => {
      if (!active) return;
      setChannels(list);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, []);

  return { channels, loading };
}
